import {useLayoutEffect, useRef} from 'react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Box, Button, Typography} from '@mui/material';
import {AccessTimeRounded, LoginRounded, LogoutRounded} from '@mui/icons-material';
import moment from 'moment';
import './attendance-ongoing-bar.scss';
import AttendanceStatusChip from './attendance-status-chip';
import {useAuth} from '../../contexts/auth/context';
import {useAttendanceTrackerStatus} from '../../contexts/attendance-tracker-status/context';
import {ScreenPaths} from '../../enums';
import {useAttendanceOngoingBarVisible} from '../../hooks/use-attendance-ongoing-bar-visible';
import {useServerNow} from '../../hooks/use-server-now';
import {translations} from '../../i18n/translations';
import {Pallete} from '../../theme/bluebird';
import {
  formatSessionDuration,
  getSessionDurationSeconds,
  isSessionOverdue,
} from '../../utils/functions/attendance-sessions';

/** CSS variable with the height of the bar, so the content and the floating buttons can make room for it */
export const ATTENDANCE_BAR_HEIGHT_VAR = '--attendance-bar-height';

/**
 * Bar above the bottom navigation of the Daily Care Log with the work session that is running and a button
 * to clock out, so it can be done from any screen. When the user must track attendance it is always shown,
 * and when there is no session running the button is to clock in. When it is optional, the bar is only shown
 * while a session is running.
 */
export default function AttendanceOngoingBar() {
  const {t} = useTranslation();
  const history = useHistory();
  const {
    state: {session: authSession},
  } = useAuth();
  const {openSession, serverTime} = useAttendanceTrackerStatus();
  const isVisible = useAttendanceOngoingBarVisible();
  const now = useServerNow(serverTime);
  const barRef = useRef<HTMLDivElement>(null);
  const texts = translations.daily_care_log.attendance_tracker;

  const affiliation = authSession?.selectedAffiliation;
  const entityId = affiliation?.entity.id;

  // Publishes the height of the bar (0 when hidden) for the content and the floating buttons
  useLayoutEffect(() => {
    const root = document.documentElement;
    const element = barRef.current;

    if (!isVisible || !element) {
      root.style.setProperty(ATTENDANCE_BAR_HEIGHT_VAR, '0px');
      return;
    }

    const update = () =>
      root.style.setProperty(ATTENDANCE_BAR_HEIGHT_VAR, `${element.offsetHeight}px`);
    update();

    // The height changes when the text wraps in narrow screens
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);
    observer?.observe(element);

    return () => {
      observer?.disconnect();
      root.style.setProperty(ATTENDANCE_BAR_HEIGHT_VAR, '0px');
    };
  }, [isVisible]);

  if (!isVisible || !entityId) {
    return null;
  }

  const basePath = `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_AttendanceTracker}`;
  const today = moment().format('YYYY-MM-DD');
  const isOverdue = !!openSession && isSessionOverdue(openSession, now);
  const state = !openSession ? 'idle' : isOverdue ? 'overdue' : 'running';

  const openDetails = () => {
    if (openSession) {
      history.push(`${basePath}/${openSession.id}?date=${today}`, {session: openSession});
    } else {
      history.push(`${basePath}?date=${today}`);
    }
  };

  const register = () =>
    history.push(
      `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_NewAttendanceRecord}?date=${today}`
    );

  return (
    <Box ref={barRef} className={`attendance-ongoing-bar attendance-ongoing-bar-${state}`}>
      <Box className="bar-content" onClick={openDetails}>
        <Box className="bar-icon">
          <AccessTimeRounded />
        </Box>
        <Box className="bar-text">
          <Typography className="bar-label">{t(texts.title)}</Typography>
          {openSession ? (
            <>
              <AttendanceStatusChip state={isOverdue ? 'overdue' : 'running'} />
              <Typography className="bar-subtitle">
                {t(texts.bar.since, {
                  date: moment(openSession.entry.registeredAt).isSame(now, 'day')
                    ? moment(openSession.entry.registeredAt).format('HH:mm')
                    : moment(openSession.entry.registeredAt).format('DD/MM HH:mm'),
                })}
                {' · '}
                {formatSessionDuration(getSessionDurationSeconds(openSession, now))}
              </Typography>
            </>
          ) : (
            <Typography className="bar-title">{t(texts.bar.not_started)}</Typography>
          )}
        </Box>
      </Box>

      <Button
        className="bar-button"
        variant="contained"
        disableElevation
        startIcon={openSession ? <LogoutRounded /> : <LoginRounded />}
        onClick={register}
        sx={{
          backgroundColor: openSession ? Pallete.red.base : Pallete.green.base,
          '&:hover': {backgroundColor: openSession ? Pallete.red.base : Pallete.green.base},
        }}
      >
        {t(openSession ? texts.register_exit : texts.register_entry)}
      </Button>
    </Box>
  );
}
